import { React, useRef, useState } from 'react';

import AddCommentRoundedIcon from '@mui/icons-material/AddCommentRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    MenuItem,
    Stack,
    TextField
} from '@mui/material';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/chamado.css';

const CATEGORIAS_POR_DEPARTAMENTO = {
    DP: [
        'GymPass',
        'Setta Runner',
        'Plano de Saúde',
        'Nota Fiscal'
    ]
};
const CATEGORIAS_COM_ANEXO_OBRIGATORIO = ['GymPass', 'Plano de Saúde', 'Nota Fiscal'];

const AbrirChamado = () => {
    const [departamento, setDepartamento] = useState('');
    const [categoria, setCategoria] = useState('');
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');
    const [arquivos, setArquivos] = useState([]);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const fileInputRef = useRef(null);

    const resetForm = () => {
        setDepartamento('');
        setCategoria('');
        setTitulo('');
        setDescricao('');
        setArquivos([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDepartamentoChange = (event) => {
        setDepartamento(event.target.value);
        setCategoria('');
    };

    const handleFiles = (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        setArquivos((currentFiles) => [...currentFiles, ...selectedFiles]);
        event.target.value = '';
    };

    const removeFile = (fileIndex) => {
        setArquivos((currentFiles) => currentFiles.filter((_, index) => index !== fileIndex));
    };

    async function submitChamado (e) {
        e.preventDefault();
        const exigeAnexo = CATEGORIAS_COM_ANEXO_OBRIGATORIO.includes(categoria);

        if (!departamento || !categoria || !titulo.trim() || !descricao.trim()) {
            setMessage('Preencha o departamento, a categoria, o título e a descrição do chamado.');
            setStatus('warning');
            setOpenAlerta(true);
            return;
        }

        if (exigeAnexo && arquivos.length === 0) {
            setMessage(`Anexe pelo menos um arquivo para abrir chamados do tipo ${categoria}.`);
            setStatus('warning');
            setOpenAlerta(true);
            return;
        }

        setIsSubmit(true);

        const body = new FormData();
        body.append('departamento', departamento);
        body.append('categoria', categoria);
        body.append('titulo', titulo);
        body.append('descricao', descricao);
        arquivos.forEach((arquivo) => body.append('files', arquivo));

        try {
            const response = await fetchFlask('abrirChamado', body);
            setMessage(response.message);
            setStatus(response.status);

            if (response.status === 'success') {
                resetForm();
            }
        } finally {
            setOpenAlerta(true);
            setIsSubmit(false);
        }
    };

    const exigeAnexo = CATEGORIAS_COM_ANEXO_OBRIGATORIO.includes(categoria);
    const criticalFields = [
        departamento,
        categoria,
        titulo.trim(),
        descricao.trim(),
        ...(exigeAnexo ? [arquivos.length > 0] : [])
    ];
    const completedFields = criticalFields.filter(Boolean).length;
    const categoriaLabel = departamento && categoria ? `${departamento} / ${categoria}` : 'Pendente';
    const anexoLabel = arquivos.length === 0
        ? 'Nenhum anexo'
        : `${arquivos.length} arquivo${arquivos.length === 1 ? '' : 's'}`;
    const anexosStatusClassName = exigeAnexo && arquivos.length === 0 ? 'is-empty' : undefined;

    return (
        <Box className='mainBox chamado-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader icon={AddCommentRoundedIcon}>
                Abrir Chamado
            </PageHeader>

            <form action='' method='POST' id='formAbrirChamado' onSubmit={submitChamado} className='chamado-layout'>
                <Box className='chamado-workspace'>
                    <Box className='chamado-intro'>
                        <Box>
                            <h2>Solicitação</h2>
                        </Box>
                        <p>
                            Informe o serviço, descreva a solicitação e anexe evidências para abrir o atendimento.
                        </p>
                    </Box>

                    <Box className='chamado-panel'>
                        <Box className='chamado-section-heading'>
                            <CategoryRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Destino do chamado</strong>
                            </Box>
                        </Box>

                        <Box className='chamado-fields-grid'>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    select
                                    label='Departamento'
                                    value={departamento}
                                    onChange={handleDepartamentoChange}
                                >
                                    {Object.keys(CATEGORIAS_POR_DEPARTAMENTO).map((departamentoOption) => (
                                        <MenuItem key={departamentoOption} value={departamentoOption}>
                                            {departamentoOption}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </FormControl>

                            <FormControl fullWidth>
                                <TextField
                                    required
                                    select
                                    label='Categoria'
                                    value={categoria}
                                    onChange={(e) => setCategoria(e.target.value)}
                                    disabled={!departamento}
                                >
                                    {(CATEGORIAS_POR_DEPARTAMENTO[departamento] || []).map((categoriaOption) => (
                                        <MenuItem key={categoriaOption} value={categoriaOption}>
                                            {categoriaOption}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='chamado-panel'>
                        <Box className='chamado-section-heading'>
                            <DescriptionRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Detalhes da solicitação</strong>
                            </Box>
                        </Box>

                        <Box className='chamado-description-grid'>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label='Título'
                                    id='titulo'
                                    value={titulo}
                                    onChange={(e) => setTitulo(e.target.value)}
                                />
                            </FormControl>

                            <FormControl fullWidth>
                                <TextField
                                    required
                                    multiline
                                    minRows={8}
                                    maxRows={12}
                                    label='Descrição'
                                    id='descricao'
                                    value={descricao}
                                    onChange={(e) => setDescricao(e.target.value)}
                                    inputProps={{ maxLength: 5000 }}
                                    helperText={`${descricao.length}/5000 caracteres`}
                                />
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='chamado-panel'>
                        <Box className='chamado-section-heading'>
                            <AttachFileRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>{exigeAnexo ? 'Anexos obrigatórios' : 'Anexos'}</strong>
                            </Box>
                        </Box>

                        <Box className='chamado-upload'>
                            <input
                                ref={fileInputRef}
                                id='arquivos'
                                type='file'
                                multiple
                                accept='image/*,application/pdf'
                                hidden
                                onChange={handleFiles}
                            />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                <Button
                                    type='button'
                                    variant='outlined'
                                    startIcon={<AttachFileRoundedIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    Anexar arquivos
                                </Button>
                                <span className='chamado-upload-hint'>
                                    {exigeAnexo
                                        ? `Para ${categoria}, anexe pelo menos um comprovante em imagem ou PDF.`
                                        : 'Imagens e PDFs podem ser anexados ao chamado.'}
                                </span>
                            </Stack>

                            {arquivos.length > 0 && (
                                <Stack className='chamado-file-list' spacing={1}>
                                    {arquivos.map((arquivo, index) => (
                                        <Stack key={`${arquivo.name}-${index}`} direction='row' alignItems='center' spacing={1}>
                                            <Chip label={arquivo.name} variant='outlined' className='chamado-file-chip' />
                                            <IconButton aria-label='Remover arquivo' size='small' onClick={() => removeFile(index)}>
                                                <DeleteRoundedIcon fontSize='small' />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Box className='chamado-summary'>
                    <Box className='chamado-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do chamado</strong>
                        </Box>
                    </Box>

                    <Box className='chamado-summary-list'>
                        <Box>
                            <span>Destino</span>
                            <strong className={!departamento || !categoria ? 'is-empty' : undefined}>{categoriaLabel}</strong>
                        </Box>
                        <Box>
                            <span>Título</span>
                            <strong className={!titulo.trim() ? 'is-empty' : undefined}>{titulo.trim() || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Descrição</span>
                            <strong>{descricao.trim() ? `${descricao.trim().length} caracteres` : 'Pendente'}</strong>
                        </Box>
                        <Box>
                            <span>Anexos</span>
                            <strong className={anexosStatusClassName}>{anexoLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='chamado-attention'>
                        <AddCommentRoundedIcon />
                        <p>
                            Revise as informações antes de enviar. O chamado será direcionado conforme departamento e categoria selecionados.
                        </p>
                    </Box>

                    <Box className='chamado-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/{criticalFields.length} preenchidos</strong>
                        </Box>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <Button type='button' variant='outlined' onClick={resetForm} disabled={isSubmit} className='chamado-clear-button'>
                            Limpar
                        </Button>
                        <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                            Abrir Chamado
                        </SubmitButton>
                    </Stack>
                </Box>
            </form>
        </Box>
    );
};

export default AbrirChamado;
